import * as React from "react";
import { LinkProps, useRouter } from "@tanstack/react-router";

/**
 * Makes a whole table row act like a link to `getDestination(item)`, an `appRoutes` entry.
 * A plain click navigates inside the SPA, so the page keeps its state and Back returns to it;
 * cmd/ctrl/shift or middle click opens a new tab, as a real link would.
 */
export function useTableRowClickthrough<Item>(
  getDestination: ((item: Item) => LinkProps) | undefined,
): ((item: Item) => React.HTMLAttributes<HTMLElement>) | undefined {
  const router = useRouter();
  if (!getDestination) return undefined;

  return (item: Item) => {
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const destination = getDestination(item);
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) {
        window.open(router.buildLocation(destination).href, "_blank");
      } else {
        void router.navigate(destination);
      }
    };

    return {
      onClick: handleClick,
      // Browsers fire auxclick, not click, for the middle button
      onAuxClick: (e: React.MouseEvent) => {
        if (e.button === 1) handleClick(e);
      },
    };
  };
}
